var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import platform from 'platform';
import { v4 as uuidv4 } from 'uuid';
import UA from '../JsSIP/UserAgent';
import { logActivity } from '../helpers/activityLog';
import appConfig from '../helpers/appConfig';
import { nvFetch } from '../helpers/nvFetch';
import { fetchCallHistory } from './callHistory';
import { incomingCall } from './calls';
import { phoneStatusSet, phonesUnsubscribed, subscribePhones, } from './contacts';
import { finaliseLogout } from './user';
let storedAudio;
const initialState = {
    isRegistered: false,
    isRegistering: false,
    isUnregistering: false,
    isFetching: false,
    error: undefined,
};
export const fetchExtensionDetails = createAsyncThunk('extension/fetchDetails', (extensionId, thunkAPI) => __awaiter(void 0, void 0, void 0, function* () {
    const { client: { activeCustomer }, } = thunkAPI.getState();
    if (!extensionId) {
        throw new Error();
    }
    const extensionURI = `/customers/${activeCustomer === null || activeCustomer === void 0 ? void 0 : activeCustomer.id}/endpoints/${extensionId}`;
    const fetchExtension = () => __awaiter(void 0, void 0, void 0, function* () {
        const fetchRes = yield nvFetch(`${extensionURI}?extended=true`);
        return (yield fetchRes.json());
    });
    const fetchSipIdentity = () => __awaiter(void 0, void 0, void 0, function* () {
        const fetchRes = yield nvFetch(`${extensionURI}/sip`);
        return (yield fetchRes.json());
    });
    const promises = [fetchExtension(), fetchSipIdentity()];
    const [extension, sip] = yield Promise.all(promises);
    return Object.assign(Object.assign({}, extension), { sip });
}));
export const registerExtension = createAsyncThunk('extension/registerExtension', (params, thunkAPI) => {
    const { username, password, domain, audio } = params;
    if (audio) {
        storedAudio = audio;
    }
    if (!username) {
        // If we've not got a username, try again in 1 second
        setTimeout(() => {
            logActivity('default', 'REGISTER_EXTENSION', {
                reason: 'REGISTER_RETRY',
            });
            thunkAPI.dispatch(registerExtension({ username, password, domain, audio }));
        }, 1000);
        return;
    }
    thunkAPI.dispatch(extensionRegistering({ username, password }));
    // TODO_V2 there's probably a better way than pulling from store here
    const brandName = thunkAPI.getState().branding.brand;
    const prefix = process.env.NODE_ENV && process.env.NODE_ENV !== 'production'
        ? `[${process.env.NODE_ENV.substring(0, 3)}]`
        : '';
    const userAgent = `${prefix}${brandName} Communicator - ${platform.description}`;
    let instanceId = localStorage.getItem('@sipInstanceId');
    if (!instanceId) {
        instanceId = uuidv4();
        localStorage.setItem('@sipInstanceId', instanceId);
    }
    const config = {
        username,
        password,
        audio,
        userAgent,
        domain,
        instance_id: instanceId,
    };
    const extension = new UA(config);
    let reconnectionInterval;
    extension.on('registered', () => {
        // Stop trying to reconnect
        clearInterval(reconnectionInterval);
        reconnectionInterval = undefined;
        thunkAPI.dispatch(extensionRegistered());
        logActivity('default', 'EXTENSION_REGISTERED', {});
        // Once the extension has registered, store it in the app config
        appConfig.extension = extension;
        extension.clearSubscriptions();
        thunkAPI.dispatch(phonesUnsubscribed());
        thunkAPI.dispatch(subscribePhones());
    });
    extension.on('unregistered', () => {
        thunkAPI.dispatch(extensionUnregistered());
        logActivity('default', 'EXTENSION_UNREGISTERED', {});
        if (extension.isActive) {
            // Start trying to reconnect
            reconnectionInterval = setInterval(() => {
                console.debug('Attempting to reconnect');
                extension.connect();
            }, 10000);
        }
    });
    extension.on('incomingCall', (call) => {
        thunkAPI.dispatch(incomingCall(call));
    });
    extension.on('userStateChanged', (user, state) => {
        thunkAPI.dispatch(phoneStatusSet({ id: user, status: state }));
    });
    extension.connect();
    appConfig.extension = extension;
});
export const switchExtension = createAsyncThunk('extension/switchExtension', (extensionId, thunkAPI) => {
    const { extension } = appConfig;
    if (!extension) {
        return;
    }
    thunkAPI.dispatch(extensionUnregistering());
    extension.isActive = false;
    extension.clearSubscriptions();
    thunkAPI.dispatch(phonesUnsubscribed());
    extension.stop();
    extension.once('unregistered', () => {
        thunkAPI
            .dispatch(fetchExtensionDetails(extensionId))
            .then((fetchedExtension) => {
            const { 
            // @ts-expect-error
            sip: { username, password, domain }, } = fetchedExtension.payload;
            console.debug(`Registering new extension ${username}`);
            logActivity('default', 'REGISTER_EXTENSION', {
                reason: 'UNREGISTERED',
                username,
            });
            thunkAPI.dispatch(registerExtension({
                username,
                password,
                domain,
                audio: storedAudio,
            }));
        });
        const overrideParams = {
            endpointId: extensionId,
            page: 1,
        };
        thunkAPI.dispatch(fetchCallHistory({ overrideParams }));
    });
});
const extensionSlice = createSlice({
    name: 'extension',
    initialState,
    reducers: {
        extensionUnregistering: (state) => {
            state.isUnregistering = true;
        },
        extensionRegistering: (state, action) => {
            state.isRegistered = false;
            state.isRegistering = true;
            state.username = action.payload.username;
            state.password = action.payload.password;
        },
        extensionRegistered: (state) => {
            state.isRegistered = true;
            state.isRegistering = false;
        },
        extensionUnregistered: (state) => {
            state.username = '';
            state.password = '';
            state.isRegistered = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchExtensionDetails.pending, (state) => {
            state.isFetching = true;
        })
            .addCase(fetchExtensionDetails.rejected, (state, action) => {
            state.isFetching = false;
            state.error = action.error;
        })
            .addCase(fetchExtensionDetails.fulfilled, (state, action) => {
            state.extension = action.payload;
            state.isFetching = false;
            state.error = undefined;
        })
            .addCase(finaliseLogout.fulfilled, () => initialState);
    },
});
export const { extensionUnregistering, extensionUnregistered, extensionRegistering, extensionRegistered, } = extensionSlice.actions;
export default extensionSlice.reducer;
